import React from 'react';
import { useRouter } from 'next/compat/router';
import _ from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useObserver } from 'mobx-react-lite';

import useMenuFilterOptions from 'hooks/use-menu-filter-options';
import useProductQueryParams from 'hooks/use-product-query-params';
import useTranslation from 'hooks/use-translation';
import { useDispensaryCategory } from 'src/hooks/use-dispensary-category';

import useUI from 'src/hooks/use-ui';
import SubcategoryPill from './subcategory-pill';
import { MultiSelectSubcategoryPills } from './multi-select-subcategory-pills';
import { Container, PillsContainer } from './styles';

/**
 * @param {{ isSpecials?: boolean, specialId?: string, specialType?: string, className?: string }} props
 */
export default function SubcategoriesPills({ isSpecials = false, specialId, specialType, ...otherProps }) {
  const category = useDispensaryCategory();
  const flags = useFlags();
  const UI = useUI();
  const isOnSubcategoryPage = useObserver(() => UI.onSubcategoryPage);

  const { subcategories } = useMenuFilterOptions({ specials: isSpecials, specialId, specialType });

  const isMultiSelectMode = (flags['ecomm.multi-filter-subcategories.rollout'] ?? false) && !isOnSubcategoryPage;

  return isMultiSelectMode ? (
    <MultiSelectSubcategoryPills subcategories={subcategories} {...otherProps} />
  ) : (
    <Pills isSpecials={isSpecials} category={category} subcategories={subcategories} {...otherProps} />
  );
}

export const Pills = ({ isSpecials, category, subcategories, ...otherProps }) => {
  const { t } = useTranslation();
  const { query } = useRouter() ?? {};
  const { queryParams } = useProductQueryParams();
  const selectedSubcategories = query.subcategory ?? queryParams.subcategories[0];

  const allLabel = t('sidebar-filters.subcategories-header', {
    defaultValue: 'All',
    category: !category || category?.key === `all` ? `Subcategories` : category.label,
  });

  if (_.isEmpty(subcategories)) {
    return null;
  }

  return (
    <Container className='subcategories-pills' {...otherProps}>
      <PillsContainer>
        <SubcategoryPill
          key='all'
          isSpecials={isSpecials}
          label={allLabel}
          selected={_.isEmpty(selectedSubcategories)}
          value={[]}
        />
        {_.map(subcategories, (subcategory) => (
          <SubcategoryPill
            key={subcategory.key}
            isSpecials={isSpecials}
            isSubcategory
            label={subcategory.label}
            selected={selectedSubcategories === subcategory.key}
            value={subcategory.value}
          />
        ))}
      </PillsContainer>
    </Container>
  );
};
